import brandLogo from "../../../../assets/images/tnp-logo-neon.png";
import { getSiteLanguage } from "../../../../shared/i18n/language";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { buildPlayerProfileShareUrl } from "../../../../shared/lib/shareLinks";
import type { AuthUser } from "../../../auth/shared/types/auth";
import type { PublicPlayerProfile } from "../../../players/shared/types/publicPlayerProfile";
import type { Achievement } from "../../shared/services/achievementsService";

const LOCALE_BY_LANGUAGE = {
  pt: "pt-BR",
  en: "en-US",
  es: "es-ES",
} as const;

export type ProfileShareStats = {
  matches: number;
  titles: number;
  runnersUp: number;
  category: string;
  rankingPosition: number | null;
};

export type ProfileShareSource = {
  user: AuthUser;
  stats: ProfileShareStats;
  avatarUrl?: string;
  achievements?: Achievement[];
  publicProfile?: PublicPlayerProfile | null;
  publicProfileUrl: string;
};

export type ProfileSharePreview = {
  blob: Blob;
  filename: string;
  url: string;
};

function initialsFromName(value?: string | null) {
  return (
    String(value || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((chunk) => chunk[0]?.toUpperCase() || "")
      .join("") || "TN"
  );
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function blobFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error(siteTexts.profile.user.dashboard.share.generateError));
    }, "image/png");
  });
}

export function buildPublicPlayerProfileUrl(playerId?: number | null) {
  const safePlayerId = Number(playerId || 0);
  if (safePlayerId > 0) return buildPlayerProfileShareUrl(safePlayerId);
  if (typeof window === "undefined") return "#/perfil";
  return `${window.location.origin}${window.location.pathname}#/perfil`;
}

function formatShareNumber(value: number) {
  return new Intl.NumberFormat(currentLocale(), {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export function getCurrentRankingPositionFromPublicProfile(
  profile?: PublicPlayerProfile | null,
) {
  const position = Number(profile?.ranking?.current?.position || 0);
  return Number.isFinite(position) && position > 0 ? position : null;
}

function formatProfileShareCategory(
  label: string | null | undefined,
  fallback: string,
) {
  const safeLabel = String(label || "").trim();
  if (!safeLabel) return fallback;
  const prefix = siteTexts.publicPlayerProfile.categoryPrefix;
  if (safeLabel.toLowerCase().startsWith(prefix.toLowerCase())) {
    return safeLabel;
  }
  return `${prefix} ${safeLabel}`;
}

function profileShareCategoryColor(
  styles: CSSStyleDeclaration,
  categoryCode?: string | null,
  fallback = "",
) {
  const normalized = String(categoryCode || "").trim().toLowerCase();
  const variableSuffix = normalized.match(/^(\d)a$/)?.[1] || normalized;
  const color =
    variableSuffix &&
    styles
      .getPropertyValue(`--color-admin-users-category-${variableSuffix}`)
      .trim();
  return color || fallback;
}

function loadShareImage(src?: string | null) {
  const safeSrc = String(src || "").trim();
  if (!safeSrc) return Promise.resolve<HTMLImageElement | null>(null);
  return new Promise<HTMLImageElement | null>((resolve) => {
    const image = new Image();
    if (!safeSrc.startsWith("data:")) image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = safeSrc;
  });
}

function fitCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxSize: number,
  minSize: number,
  weight = 900,
) {
  let size = maxSize;
  do {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    if (context.measureText(text).width <= maxWidth) return size;
    size -= 2;
  } while (size >= minSize);
  return minSize;
}

function truncateCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const safeText = String(text || "").trim();
  if (!safeText || context.measureText(safeText).width <= maxWidth) {
    return safeText;
  }
  let truncated = safeText;
  while (
    truncated.length > 1 &&
    context.measureText(`${truncated.trimEnd()}...`).width > maxWidth
  ) {
    truncated = truncated.slice(0, -1);
  }
  return `${truncated.trimEnd()}...`;
}

function wrapCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) {
  const words = String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const lines: string[] = [];
  let currentLine = "";

  const pushWord = (word: string) => {
    if (context.measureText(word).width <= maxWidth) {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (!currentLine || context.measureText(nextLine).width <= maxWidth) {
        currentLine = nextLine;
        return;
      }
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = "";
    }

    let chunk = "";
    Array.from(word).forEach((char) => {
      const nextChunk = `${chunk}${char}`;
      if (!chunk || context.measureText(nextChunk).width <= maxWidth) {
        chunk = nextChunk;
        return;
      }
      lines.push(chunk);
      chunk = char;
    });
    if (chunk) currentLine = chunk;
  };

  words.forEach(pushWord);
  if (currentLine) lines.push(currentLine);
  return lines;
}

function drawWrappedProfileShareText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxHeight,
  maxSize,
  minSize,
  fillStyle,
  weight = 800,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxHeight: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  weight?: number;
}) {
  const safeText = String(text || "").trim();
  if (!safeText) return;

  let size = maxSize;
  let lines: string[] = [];
  let lineHeight = Math.round(size * 1.18);
  do {
    context.font = `${weight} ${size}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    lines = wrapCanvasText(context, safeText, maxWidth);
    lineHeight = Math.round(size * 1.18);
    if (lines.length * lineHeight <= maxHeight) break;
    size -= 1;
  } while (size >= minSize);

  context.textAlign = "left";
  context.fillStyle = fillStyle;
  lines.forEach((line, index) => {
    context.fillText(line, x, y + index * lineHeight);
  });
}

function drawProfileShareText({
  context,
  text,
  x,
  y,
  maxWidth,
  maxSize,
  minSize,
  fillStyle,
  align = "left",
  weight = 900,
}: {
  context: CanvasRenderingContext2D;
  text: string;
  x: number;
  y: number;
  maxWidth: number;
  maxSize: number;
  minSize: number;
  fillStyle: string | CanvasGradient;
  align?: CanvasTextAlign;
  weight?: number;
}) {
  fitCanvasText(context, text, maxWidth, maxSize, minSize, weight);
  context.textAlign = align;
  context.fillStyle = fillStyle;
  context.fillText(truncateCanvasText(context, text, maxWidth), x, y);
}

function drawProfileShareAvatar({
  context,
  image,
  name,
  x,
  y,
  radius,
  blue,
  pink,
}: {
  context: CanvasRenderingContext2D;
  image: HTMLImageElement | null;
  name: string;
  x: number;
  y: number;
  radius: number;
  blue: string;
  pink: string;
}) {
  context.save();
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2);
  context.clip();
  if (image) {
    const side = Math.min(image.width, image.height);
    context.drawImage(
      image,
      (image.width - side) / 2,
      (image.height - side) / 2,
      side,
      side,
      x - radius,
      y - radius,
      radius * 2,
      radius * 2,
    );
  } else {
    const gradient = context.createLinearGradient(
      x - radius,
      y - radius,
      x + radius,
      y + radius,
    );
    gradient.addColorStop(0, blue);
    gradient.addColorStop(1, pink);
    context.fillStyle = gradient;
    context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
    context.fillStyle = "#ffffff";
    context.font = `900 ${Math.max(42, radius * 0.58)}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(initialsFromName(name), x, y + 2);
  }
  context.restore();

  const border = context.createLinearGradient(
    x - radius,
    y - radius,
    x + radius,
    y + radius,
  );
  border.addColorStop(0, pink);
  border.addColorStop(1, blue);
  context.strokeStyle = border;
  context.lineWidth = 10;
  context.beginPath();
  context.arc(x, y, radius + 6, 0, Math.PI * 2);
  context.stroke();
}

function drawProfileShareStat({
  context,
  label,
  value,
  x,
  y,
  blue,
  pink,
  tone = "blue",
}: {
  context: CanvasRenderingContext2D;
  label: string;
  value: string;
  x: number;
  y: number;
  blue: string;
  pink: string;
  tone?: "blue" | "pink" | "gold";
}) {
  const accent =
    tone === "gold" ? "#e7c565" : tone === "pink" ? pink : blue;
  context.fillStyle = "rgba(7,22,42,0.72)";
  context.strokeStyle = "rgba(132,166,198,0.34)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(x, y, 396, 172, 24);
  context.fill();
  context.stroke();

  context.fillStyle = accent;
  context.beginPath();
  context.roundRect(x + 30, y + 34, 12, 92, 6);
  context.fill();

  context.fillStyle = "#ffffff";
  context.font =
    "900 48px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.textBaseline = "alphabetic";
  context.fillText(truncateCanvasText(context, value, 300), x + 66, y + 72);
  context.fillStyle = "rgba(222,235,245,0.82)";
  context.font =
    "700 23px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(truncateCanvasText(context, label, 310), x + 66, y + 126);
}

export async function createProfileShareBlob({
  user,
  stats,
  avatarUrl,
  achievements = [],
  publicProfile,
  publicProfileUrl,
}: ProfileShareSource) {
  const dashboardTexts = siteTexts.profile.user.dashboard;
  const shareTexts = dashboardTexts.share;
  const publicTexts = siteTexts.publicPlayerProfile;
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1920;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas indisponivel.");

  const styles = getComputedStyle(document.documentElement);
  const blue =
    styles.getPropertyValue("--color-brand-blue").trim() || "#2b9cdf";
  const pink =
    styles.getPropertyValue("--color-brand-pink").trim() || "#e53682";
  const categoryColor = profileShareCategoryColor(
    styles,
    publicProfile?.player.categoryCode || user.category,
    pink,
  );
  const profileName =
    publicProfile?.player.displayName || user.name || shareTexts.athleteFallback;
  const profileCategory = formatProfileShareCategory(
    publicProfile?.player.categoryLabel || stats.category,
    shareTexts.categoryFallback,
  );
  const profileStats = publicProfile?.stats;
  const rankingGenderLabel =
    user.sex === "female"
      ? siteTexts.common.sex.female
      : user.sex === "male"
        ? siteTexts.common.sex.male
        : siteTexts.common.filters.allSexes;
  const hasRankingPosition = Boolean(stats.rankingPosition);
  const rankingText = stats.rankingPosition
    ? `#${formatShareNumber(stats.rankingPosition)}`
    : shareTexts.noRankingPosition;
  const rankingSubtext = hasRankingPosition
    ? `${siteTexts.ranking.page.sourceTabs.official} • ${siteTexts.ranking.page.modeTabs.all}`
    : shareTexts.noRankingHint;
  const publicAchievements = (publicProfile?.achievements || []).slice(0, 3);
  const [logoImage, avatarImage] = await Promise.all([
    loadShareImage(brandLogo),
    loadShareImage(publicProfile?.player.avatarUrl || avatarUrl || user.avatarUrl),
  ]);

  const bg = context.createLinearGradient(0, 0, 1080, 1920);
  bg.addColorStop(0, "#020817");
  bg.addColorStop(0.48, "#06172d");
  bg.addColorStop(1, "#16011f");
  context.fillStyle = bg;
  context.fillRect(0, 0, 1080, 1920);

  context.globalAlpha = 0.74;
  context.strokeStyle = blue;
  context.lineWidth = 2;
  context.beginPath();
  context.arc(160, 340, 420, 4.8, 6.25);
  context.stroke();
  context.strokeStyle = pink;
  context.beginPath();
  context.arc(980, 1240, 620, 2.9, 5.15);
  context.stroke();
  context.globalAlpha = 1;

  context.fillStyle = "rgba(255,255,255,0.035)";
  context.strokeStyle = "rgba(132,166,198,0.34)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(72, 92, 936, 1660, 34);
  context.fill();
  context.stroke();

  if (logoImage) {
    context.drawImage(logoImage, 112, 134, 168, 118);
  }

  context.textAlign = "right";
  context.fillStyle = "rgba(222,235,245,0.84)";
  context.font =
    "800 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.fillText(shareTexts.journey, 948, 176);

  drawProfileShareAvatar({
    context,
    image: avatarImage,
    name: profileName,
    x: 540,
    y: 440,
    radius: 138,
    blue,
    pink,
  });

  drawProfileShareText({
    context,
    text: profileName,
    x: 540,
    y: 660,
    maxWidth: 780,
    maxSize: 62,
    minSize: 38,
    fillStyle: "#ffffff",
    align: "center",
  });

  context.strokeStyle = categoryColor;
  context.fillStyle = "rgba(7,22,42,0.78)";
  context.lineWidth = 2;
  context.beginPath();
  context.roundRect(300, 694, 480, 58, 29);
  context.fill();
  context.stroke();
  drawProfileShareText({
    context,
    text: profileCategory,
    x: 540,
    y: 733,
    maxWidth: 420,
    maxSize: 28,
    minSize: 20,
    fillStyle: categoryColor,
    align: "center",
    weight: 900,
  });

  const rankGradient = context.createLinearGradient(112, 790, 968, 922);
  rankGradient.addColorStop(0, pink);
  rankGradient.addColorStop(1, blue);
  context.fillStyle = "rgba(7,22,42,0.78)";
  context.strokeStyle = rankGradient;
  context.lineWidth = 3;
  context.beginPath();
  context.roundRect(112, 790, 856, 132, 30);
  context.fill();
  context.stroke();
  context.fillStyle = "#ffffff";
  context.font =
    "900 38px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(
    truncateCanvasText(
      context,
      `${siteTexts.ranking.page.titleFallbacks.ranking} geral ${rankingGenderLabel.toLowerCase()}`,
      560,
    ),
    152,
    842,
  );
  context.font = hasRankingPosition
    ? "900 44px system-ui, -apple-system, BlinkMacSystemFont, sans-serif"
    : "900 34px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "right";
  context.fillText(
    hasRankingPosition
      ? truncateCanvasText(context, rankingText, 250)
      : rankingText,
    928,
    842,
  );
  context.fillStyle = "rgba(222,235,245,0.82)";
  context.font =
    "800 25px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  context.textAlign = "left";
  context.fillText(truncateCanvasText(context, rankingSubtext, 560), 152, 884);
  context.textAlign = "right";
  context.fillText(
    hasRankingPosition
      ? shareTexts.rankingPositionLabel
      : shareTexts.rankingStatusLabel,
    928,
    884,
  );

  drawProfileShareStat({
    context,
    label: publicTexts.stats.participations,
    value: formatShareNumber(profileStats?.participations ?? stats.matches),
    x: 112,
    y: 995,
    blue,
    pink,
    tone: "blue",
  });
  drawProfileShareStat({
    context,
    label: publicTexts.stats.points,
    value: formatShareNumber(profileStats?.points ?? 0),
    x: 572,
    y: 995,
    blue,
    pink,
    tone: "pink",
  });
  drawProfileShareStat({
    context,
    label: publicTexts.stats.wins,
    value: formatShareNumber(profileStats?.wins ?? 0),
    x: 112,
    y: 1200,
    blue,
    pink,
    tone: "blue",
  });
  drawProfileShareStat({
    context,
    label: publicTexts.stats.titles,
    value: formatShareNumber(profileStats?.titles ?? stats.titles),
    x: 572,
    y: 1200,
    blue,
    pink,
    tone: "gold",
  });

  const mainAchievements =
    publicAchievements.length > 0
      ? publicAchievements
      : achievements
          .filter((item) => item.status === "unlocked")
          .sort((a, b) => {
            if (a.rarity !== b.rarity) return a.rarity === "rare" ? -1 : 1;
            return String(b.unlockedAt || "").localeCompare(
              String(a.unlockedAt || ""),
            );
          })
          .slice(0, 3);

  if (mainAchievements.length > 0) {
    context.fillStyle = "rgba(222,235,245,0.86)";
    context.font =
      "900 30px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
    context.textAlign = "left";
    context.fillText(publicTexts.achievements.title, 112, 1450);

    mainAchievements.forEach((achievement, index) => {
      const x = 112 + index * 286;
      const y = 1490;
      context.fillStyle =
        achievement.rarity === "rare"
          ? "rgba(231,197,101,0.18)"
          : "rgba(7,22,42,0.72)";
      context.strokeStyle =
        achievement.rarity === "rare" ? "#e7c565" : "rgba(132,166,198,0.34)";
      context.lineWidth = 2;
      context.beginPath();
      context.roundRect(x, y, 260, 148, 22);
      context.fill();
      context.stroke();

      context.fillStyle = achievement.rarity === "rare" ? "#e7c565" : blue;
      context.font = "36px 'Material Symbols Outlined'";
      context.textAlign = "left";
      context.textBaseline = "alphabetic";
      context.fillText(
        String(achievement.icon || "workspace_premium"),
        x + 18,
        y + 50,
      );

      drawWrappedProfileShareText({
        context,
        text: String(achievement.name || ""),
        x: x + 18,
        y: y + 78,
        maxWidth: 218,
        maxHeight: 64,
        maxSize: 20,
        minSize: 14,
        fillStyle: "#ffffff",
        weight: 800,
      });
    });
  }

  const footerGradient = context.createLinearGradient(330, 1842, 750, 1842);
  footerGradient.addColorStop(0, blue);
  footerGradient.addColorStop(1, pink);
  drawProfileShareText({
    context,
    text: "www.todosnopadel.com.br",
    x: 540,
    y: 1842,
    maxWidth: 820,
    maxSize: 30,
    minSize: 22,
    fillStyle: footerGradient,
    align: "center",
    weight: 900,
  });

  const filename = `perfil-tnp-${
    String(user.name || "atleta")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "atleta"
  }.png`;

  return {
    blob: await blobFromCanvas(canvas),
    filename,
  };
}

function currentLocale() {
  return LOCALE_BY_LANGUAGE[getSiteLanguage()] || "pt-BR";
}
